import styled from '@emotion/styled';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

type ContextMenuAction = (triggerElement?: HTMLElement | null) => void;

interface IProps {
  triggerElement: HTMLElement | null;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  options: { [key: string]: ContextMenuAction };
}

export const ContextMenu = ({
  triggerElement,
  isOpen,
  setIsOpen,
  options,
}: IProps) => {
  const [position, setPosition] = useState({ left: -1000, top: -1000 });
  const self = useRef<HTMLDivElement>(null);
  const minSelfWidth = 129;

  const setCoords = (element?: HTMLElement | null) => {
    if (!element) {
      setPosition({ left: -1000, top: -1000 });
      return;
    }

    const selfElement = self.current;
    if (!selfElement) return;

    const { innerWidth, innerHeight } = window;

    const {
      left: triggerLeft,
      bottom: triggerBottom,
      top: triggerTop,
      right: triggerRight,
    } = element.getBoundingClientRect();

    let { width: selfWidth, height: selfHeight } =
      selfElement.getBoundingClientRect();

    selfWidth = selfWidth || minSelfWidth;

    const left =
      selfWidth + triggerLeft > innerWidth
        ? triggerRight - selfWidth
        : triggerLeft;

    const top =
      selfHeight + triggerBottom > innerHeight
        ? triggerTop - selfHeight
        : triggerBottom;

    setPosition({ left, top });
  };

  useLayoutEffect(() => {
    if (isOpen) {
      setCoords(triggerElement);
    }
  }, [isOpen, triggerElement]);

  useEffect(() => {
    const clickAway = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      if (
        self.current &&
        !self.current.contains(target) &&
        triggerElement &&
        !triggerElement.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('click', clickAway);

    return () => {
      document.removeEventListener('click', clickAway);
    };
  }, [setIsOpen, triggerElement]);

  const Div = useMemo(
    () => styled.div`
      min-width: 129px;
      border-radius: 10px;
      background: #eeebeb;
      z-index: 1000;
      position: fixed;
      top: ${position.top}px;
      left: ${position.left}px;
      display: ${isOpen ? 'flex' : 'none'};
      flex-direction: column;
      justify-content: start;
      align-items: center;
      padding: 8px 0px;
    `,
    [position, isOpen]
  );

  const Span = useMemo(
    () => styled.span`
      align-self: stretch;
      cursor: pointer;
      padding-left: 8px;

      &:hover {
        background: #d9d9d9;
      }
    `,
    []
  );

  const callAction = (action: ContextMenuAction) => {
    setIsOpen(false);
    action(triggerElement);
  };

  return (
    <Div ref={self}>
      {Object.keys(options).map(key => (
        <Span key={key} onClick={() => callAction(options[key])}>
          {key}
        </Span>
      ))}
    </Div>
  );
};

export default ContextMenu;
